/**
 * What the SHARED components cannot know, supplied once by whichever app is running.
 *
 * These components render the same member in two very different places. The web app
 * is a full-page SPA on an admin session; the Helper is a 340px side panel on a
 * 90-day device token with a deliberately narrower scope. Everything that differs
 * between those two is in this file, and everything that does not is a component.
 *
 * WHY MODULE-LEVEL AND NOT SVELTE CONTEXT
 * ---------------------------------------
 * Context needs a provider component wrapping every tree that uses one of these, in
 * both apps, including the extension's side panel and its thread view. That is a
 * provider people forget, and the failure is a runtime "no host configured" in a
 * tooltip nobody was looking at. A module-level registry is set once at startup and
 * cannot be forgotten per-tree — and it is the same shape both apps already use for
 * `community.svelte.ts`, `nav.svelte.ts` and `roles.svelte.ts`, so it is not a new
 * idea in this codebase, just one applied across two of them.
 *
 * WHAT MAY GO IN HERE
 * -------------------
 * Capabilities, not data. "How do I open a link", "how do I load a member" — never
 * "who is the current member", which is a prop. If something here starts looking
 * like state the component should be rendering, it belongs in the view model below.
 */

import { configureApi } from '@api/transport';

export interface UiHost {
  /**
   * One authenticated GET, the running app's way.
   *
   * `path` is the REAL backend path — `/v1/communities/{id}/members/{id}`, exactly
   * what you would read in the API docs. There is no prefix to add and none implied,
   * because the two apps' own clients disagreed about that: the web app's client
   * took a path relative to `/v1` and the extension's takes the whole thing, so a
   * shared "v1-relative" convention meant one of them built `/v1/v1/...`. It did,
   * and the 404 surfaced as a hovercard spinning forever.
   *
   * TRANSPORT, not data. Both apps call the same route for the same shape and differ
   * only in what they put on the request — an admin session here, a 90-day device
   * token there — so the package owns the route and asks only for a way to send it.
   *
   * Contracted to REJECT on a non-2xx like an ordinary client. Callers here swallow
   * it: a hovercard that cannot load is a tooltip that does not appear.
   */
  apiGet<T>(path: string, params?: Record<string, string | number | string[]>): Promise<T>;

  /**
   * The same request, uninterpreted — auth attached, response handed back as-is.
   *
   * For the one call that no JSON client can express: the suggestion stream, which
   * POSTs a body and reads SSE frames. Both apps had their own reader and their own
   * copy of the frame grammar; this exists so the package can own that once and ask
   * only for a way to send the request.
   *
   * Contracted NOT to throw on a non-2xx — the caller reads the status and the error
   * envelope itself. It may still reject on a transport failure, like `fetch`.
   */
  apiFetch(path: string, init?: RequestInit): Promise<Response>;

  /**
   * One authenticated WRITE, the running app's way. POST, PATCH, PUT or DELETE.
   *
   * One method rather than four, because the apps differ in transport and in
   * nothing else: `apiPost` and `apiPatch` in the web app, and `apiPost` in the
   * Helper, are the same request with a different verb in it.
   *
   * Same contract as `apiGet`: the REAL backend path, rejects on a non-2xx.
   */
  apiSend<T>(
    method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    path: string,
    body?: unknown,
    params?: Record<string, string | number | string[]>,
  ): Promise<T>;

  /**
   * A stable key for whatever scopes a card, so the cache cannot serve one
   * community's member under another's id.
   *
   * The web app scopes members by community and the Helper does too, but neither the
   * component nor this interface should know that — it asks for a key and compares
   * strings. Null means "not resolvable yet", and nothing is cached under it.
   */
  memberCardScope(): string | null;

  /** Where a member's profile lives, for a real `<a href>` — so middle-click and
   *  cmd-click keep working. Null when this app has nowhere to send them. */
  memberProfileHref(memberId: number): string | null;

  /**
   * Follow that link on a PLAIN click, the app's own way: SPA navigation in the web
   * panel, a new tab from the extension. Modified clicks are never routed here — the
   * component lets the browser have them, which is what keeps cmd-click honest.
   */
  openMemberProfile(memberId: number): void;

  /**
   * Follow a link in rendered Skool content, the app's way.
   *
   * The web app returns false and lets the browser do what an `<a href>` does. The
   * extension returns true after opening the URL itself, because a side panel is not
   * a place to navigate away from — a plain `target="_blank"` from a panel behaves
   * differently there, which is the ONLY difference these two rendering components
   * ever had between them.
   *
   * Modified clicks never reach here: the component leaves those to the browser, so
   * cmd-click keeps meaning "new tab" on both surfaces.
   *
   * @returns true when the host handled it and the component should preventDefault.
   */
  openLink(href: string): boolean;

  /** This member's contributor role, for the avatar ring, or null. Both apps keep
   *  their own role map because they load it from different places. */
  roleOf(memberId: number | null | undefined): string | null;

  /**
   * Who a reply actually publishes as, and anything standing in its way.
   *
   * A lookup, in the same sense as `roleOf`: the composer renders it, neither app's
   * answer is a prop, and both already hold it globally — the web app in its Helper
   * store, the Helper in its own live identity. Passing it instead meant threading
   * three props through every intermediate component (the panel drilled them from
   * App through ThreadView), which is a lot of plumbing for one avatar.
   *
   * `warning` is a whole sentence, not a state name, because the reason a reply
   * won't go out as-is differs per app and only the app can phrase it. Null means
   * "nothing is wrong", never "don't know" — an unknown state must not fabricate a
   * warning, since the reply still queues and the outbox shows the real outcome.
   */
  postsAs(): ReplyIdentity | null;

  /**
   * Turn a failed request into what the owner should read, or null if the host has
   * taken it from here.
   *
   * The two apps' policies genuinely differ and neither belongs in a component: the
   * web app navigates away on a 401 or 402, so the composer must show NOTHING and
   * stop; the Helper has no routes to navigate to and must never unpair on an API
   * error, so it turns the same failure into a sentence. Returning null means
   * "handled, say nothing".
   */
  apiErrorMessage(err: unknown, fallback: string): string | null;

  /** Product analytics, where the app has any. */
  track(event: string, props?: Record<string, unknown>): void;
}

/** Who the reply goes out as. See `UiHost.postsAs`. */
export interface ReplyIdentity {
  name: string | null;
  pictureUrl: string | null;
  warning: string | null;
}

/**
 * Sensible nothing.
 *
 * A component rendered before `configureUi` runs must degrade, not explode: no card,
 * no ring, no link. That is a visibly empty tooltip — annoying and obvious in
 * development, harmless in production — where a throw would take down whatever list
 * the avatar happened to be sitting in.
 */
let host: UiHost = {
  apiGet: async () => {
    throw new Error('@ui: configureUi() has not run — no transport installed');
  },
  apiFetch: async () => {
    throw new Error('@ui: configureUi() has not run — no transport installed');
  },
  apiSend: async () => {
    throw new Error('@ui: configureUi() has not run — no transport installed');
  },
  memberCardScope: () => null,
  memberProfileHref: () => null,
  openLink: () => false,
  openMemberProfile: () => {},
  roleOf: () => null,
  postsAs: () => null,
  // The message, unless the host has an opinion. Never null by default: swallowing
  // an error is a choice only an app that navigated away is entitled to make.
  apiErrorMessage: (err, fallback) => (err instanceof Error ? err.message : fallback),
  track: () => {},
};

/**
 * Install the running app's capabilities. Call once, at startup, before render.
 *
 * The three transport capabilities are FORWARDED to `@api`, which is what the shared
 * clients actually call. One `configureUi` still configures everything, so no app has
 * to know the client half moved into its own package — and there is exactly one place
 * a request can be built, rather than two that could drift apart.
 */
export function configureUi(h: Partial<UiHost>): void {
  host = { ...host, ...h };
  const t: Parameters<typeof configureApi>[0] = {};
  if (h.apiGet) t.get = h.apiGet;
  if (h.apiSend) t.send = h.apiSend;
  if (h.apiFetch) t.raw = h.apiFetch;
  if (Object.keys(t).length) configureApi(t);
}

/** The active host. Read through a function rather than exported directly, so a
 *  component cannot capture the default at module-init and keep it forever. */
export function ui(): UiHost {
  return host;
}

/**
 * Handle a plain click on an `<a href>` the app's way; leave every other click to
 * the browser.
 *
 * The four-modifier test is the whole point and is what makes cmd-click, ctrl-click,
 * shift-click and middle-click keep meaning what they mean everywhere else. It was
 * written out at each link — inline, in a template, four times — which is three
 * chances to forget one of the modifiers.
 *
 * Attach as `onclick={(e) => followLink(e, href)}`.
 */
export function followLink(e: MouseEvent, href: string): void {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
  if (ui().openLink(href)) e.preventDefault();
}
